import React from "react";
import { useSelector } from "react-redux";

import { RootState } from "store";
import { selectIsCurrentPairMarketOpen } from "store/slices/tradingSlice";
import { useGetUserQuery } from "entities/user";
import {
  useDuration,
  useAmount,
  usePlaceOrder,
  useSwitchTime,
} from "widgets/traiding-panel";
import { TimeControl } from "./TimeControl";
import { AmountControl } from "./AmountControl";
import { TradeButtons } from "./TradeButtons";
import { CurrentPayout } from "features/select-pair";

import doubleArrowIcon from "assets/svg/double-arrow.svg";
import styles from "./TradingPanel.module.css";

interface TradingPanelProps {
  title: string;
  onButtonToggle: () => void;
}

export const TradingPanel: React.FC<TradingPanelProps> = ({
  title,
  onButtonToggle,
}) => {
  const { selectedPair, settings } = useSelector(
    (state: RootState) => state.trading,
  );
  const { isConnected } = useSelector((state: RootState) => state.websocket);
  const isMarketOpen = useSelector((state: RootState) =>
    selectIsCurrentPairMarketOpen(state.trading),
  );

  const {
    duration,
    hours,
    minutes,
    seconds,
    adjustDuration,
    adjustField,
    setDuration,
  } = useDuration();
  const {
    amount,
    displayValue,
    handleChange,
    handleBlur,
    setAmount,
    increase,
    decrease,
  } = useAmount();
  const { placeOrder, isPlacingOrder, orderInProgress } = usePlaceOrder();
  const {
    enabled: switchTimeEnabled,
    toggle: toggleSwitchTime,
    expiryClockTime,
  } = useSwitchTime();

  const { data: user } = useGetUserQuery();
  const canSwitchTime = user?.status.switch_time ?? false;

  const isOrderButtonDisabled =
    !selectedPair ||
    !isConnected ||
    isPlacingOrder ||
    !isMarketOpen ||
    (!settings.demo_balance && !settings.real_balance);

  return (
    <div className={styles.panel}>
      <h3 className={styles.title}>{title}</h3>

      <button
        className={`${styles.button} ${styles.toggleButton} ${styles.toggleButtonClose}`}
        onClick={onButtonToggle}
      >
        <img src={doubleArrowIcon} alt="Hide" />
      </button>

      <TimeControl
        hours={hours}
        minutes={minutes}
        seconds={seconds}
        duration={duration}
        adjustDuration={adjustDuration}
        adjustField={adjustField}
        setDuration={setDuration}
        canSwitchTime={canSwitchTime}
        switchTimeEnabled={switchTimeEnabled}
        onToggleSwitchTime={toggleSwitchTime}
        expiryClockTime={expiryClockTime}
      />

      <AmountControl
        amount={amount}
        displayValue={displayValue}
        handleChange={handleChange}
        handleBlur={handleBlur}
        setAmount={setAmount}
        increase={increase}
        decrease={decrease}
      />

      <CurrentPayout />

      <TradeButtons
        selectedPair={selectedPair}
        amount={amount}
        duration={duration}
        placeOrder={placeOrder}
        isOrderButtonDisabled={isOrderButtonDisabled}
        orderInProgress={orderInProgress}
      />
    </div>
  );
};
